import type { InertiaFormProps } from '@inertiajs/react'
import { Button } from '@/shared/ui/button'
import { DatePickerInput } from '@/shared/ui/date-picker-input'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { SearchableSelect } from '@/shared/ui/searchable-select'
import { ScanLine } from 'lucide-react'
import BarcodeScanner from '@/shared/components/BarcodeScanner'
import InstrumentTagCombobox from '@/shared/components/InstrumentTagCombobox'
import { WeeklyScheduleGrid } from '@/shared/ui/weekly-schedule-grid'
import type { WeekdaySchedule } from '@/shared/ui/weekly-schedule-grid'
import { t } from '@/shared/lib/i18n'
import { useOrgCascadeFilters } from '@/shared/hooks/useOrgCascadeFilters'

export interface InstrumentFormSelectOption {
  value: number
  label: string
  siteId?: number | null
  departmentId?: number | null
}

export interface InstrumentFormErrors {
  [key: string]: string | string[] | undefined
}

export interface InstrumentFormData {
  instrumentLabel: string
  serialNumber: string | null
  barcodeId: string | null
  cost: string | null
  dateOfPurchase: string | null
  instrumentTypeId: string
  followParentSchedule: boolean
  customDaySchedule: WeekdaySchedule
  siteId: string
  departmentId: string
  unitId: string
  tagIds: number[]
  /** REBUILD-531: per-instrument Log Authentication override ('OL' | 'PR'); '' inherits tenant. */
  logAuthenticationCode?: string | null
}

export interface InstrumentTagOption {
  value: number
  label: string
}

interface Props {
  form: InertiaFormProps<InstrumentFormData>
  errors: InstrumentFormErrors
  instrumentTypeOptions: InstrumentFormSelectOption[]
  siteOptions: InstrumentFormSelectOption[]
  departmentOptions: InstrumentFormSelectOption[]
  unitOptions: InstrumentFormSelectOption[]
  parentDaySchedule: WeekdaySchedule | undefined
  parentPreviewPending?: boolean
  parentPreviewFailed?: boolean
  /** Retry exhausted parent preview while Follow Unit stays ON. */
  onParentPreviewRetry?: () => void
  isTypeLocked?: boolean
  initialTags?: InstrumentTagOption[]
  idPrefix?: string
  hideTypeField?: boolean
  /**
   * REBUILD-531: whether the tenant has open logging ('OL') enabled. The per-instrument
   * Log Authentication override select is shown only when this is true.
   */
  tenantOpenLoggingEnabled?: boolean
}

const FieldError = ({ errors, field }: { errors: InstrumentFormErrors; field: string }) => {
  const value = errors[field]
  const message = Array.isArray(value) ? value[0] : value
  if (!message) return null
  return <p className="text-sm text-destructive mt-1">{message}</p>
}

/**
 * Presentational field set shared by the full instrument page (Instruments/Form)
 * and the "Add Instrument" modal (REBUILD-103). It owns no data fetching or
 * submission — the parent supplies an Inertia `form` object and the option lists.
 */
export const InstrumentFormFields = ({
  form,
  errors,
  instrumentTypeOptions,
  siteOptions,
  departmentOptions,
  unitOptions,
  parentDaySchedule,
  parentPreviewPending = false,
  parentPreviewFailed = false,
  onParentPreviewRetry,
  isTypeLocked = false,
  initialTags = [],
  idPrefix = '',
  hideTypeField = false,
  tenantOpenLoggingEnabled = false,
}: Props) => {
  const fieldId = (name: string) => `${idPrefix}${name}`

  const {
    filteredDepartmentOptions,
    filteredUnitOptions,
    handleSiteChange,
    handleDepartmentChange,
  } = useOrgCascadeFilters({
    siteId: form.data.siteId,
    setSiteId: (val) => form.setData('siteId', val),
    departmentId: form.data.departmentId,
    setDepartmentId: (val) => form.setData('departmentId', val),
    departmentOptions,
    setUnitId: (val) => form.setData('unitId', val),
    unitOptions,
    emptyValue: '',
    requireParent: true,
  })

  return (
    <>
      <div className="space-y-1.5">
        <Label htmlFor={fieldId('instrumentLabel')}>{t('instruments.form.labelField')} *</Label>
        <Input
          id={fieldId('instrumentLabel')}
          placeholder={t('instruments.form.labelFieldPlaceholder')}
          value={form.data.instrumentLabel}
          onChange={(e) => form.setData('instrumentLabel', e.target.value)}
        />
        <FieldError errors={errors} field="instrument_label" />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={fieldId('serialNumber')}>{t('instruments.form.serialNumberField')}</Label>
        <Input
          id={fieldId('serialNumber')}
          placeholder={t('instruments.form.serialNumberPlaceholder')}
          value={form.data.serialNumber ?? ''}
          onChange={(e) => form.setData('serialNumber', e.target.value)}
        />
        <FieldError errors={errors} field="serial_number" />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={fieldId('barcodeId')}>{t('instruments.form.barcodeField')}</Label>
        <div className="flex gap-2">
          <Input
            id={fieldId('barcodeId')}
            placeholder={t('instruments.form.barcodePlaceholder')}
            value={form.data.barcodeId ?? ''}
            onChange={(e) => form.setData('barcodeId', e.target.value)}
            className="flex-1"
          />
          <BarcodeScanner
            onScan={(scannedValue) => form.setData('barcodeId', scannedValue)}
            trigger={
              <Button
                type="button"
                variant="outline"
                size="icon"
                title={t('barcodeScanner.scanButton')}
                aria-label={t('barcodeScanner.scanButton')}
              >
                <ScanLine className="h-4 w-4" />
              </Button>
            }
          />
        </div>
        <FieldError errors={errors} field="barcode_id" />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={fieldId('cost')}>{t('instruments.form.costField')}</Label>
        <Input
          id={fieldId('cost')}
          placeholder={t('instruments.form.costPlaceholder')}
          value={form.data.cost ?? ''}
          onChange={(e) => form.setData('cost', e.target.value)}
        />
        <FieldError errors={errors} field="cost" />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={fieldId('dateOfPurchase')}>
          {t('instruments.form.dateOfPurchaseField')}
        </Label>
        <DatePickerInput
          id={fieldId('dateOfPurchase')}
          value={form.data.dateOfPurchase ?? ''}
          onChange={(val) => form.setData('dateOfPurchase', val)}
        />
        <FieldError errors={errors} field="date_of_purchase" />
      </div>

      {!hideTypeField && (
        <div className="space-y-1.5">
          <Label htmlFor={fieldId('instrumentTypeId')}>{t('instruments.form.typeField')} *</Label>
          <SearchableSelect
            id={fieldId('instrumentTypeId')}
            value={form.data.instrumentTypeId ?? ''}
            disabled={isTypeLocked}
            onValueChange={(value) => form.setData('instrumentTypeId', value)}
            placeholder={t('instruments.form.selectType')}
            aria-label={t('instruments.form.typeField')}
            aria-required
            options={instrumentTypeOptions.map((opt) => ({
              value: opt.value.toString(),
              label: opt.label,
            }))}
          />
          {isTypeLocked && (
            <p className="text-sm text-muted-foreground">{t('instruments.form.typeLockedHint')}</p>
          )}
          <FieldError errors={errors} field="instrumentTypeId" />
        </div>
      )}

      <div className="space-y-1.5">
        <Label>{t('instruments.form.siteField')} *</Label>
        <SearchableSelect
          value={form.data.siteId}
          onValueChange={handleSiteChange}
          placeholder={t('instruments.form.selectSite')}
          options={siteOptions.map((opt) => ({
            value: opt.value.toString(),
            label: opt.label,
          }))}
        />
        <FieldError errors={errors} field="siteId" />
      </div>

      <div className="space-y-1.5">
        <Label>{t('instruments.form.departmentField')} *</Label>
        <SearchableSelect
          value={form.data.departmentId}
          onValueChange={handleDepartmentChange}
          disabled={!form.data.siteId}
          placeholder={t('instruments.form.selectDepartment')}
          options={filteredDepartmentOptions.map((opt) => ({
            value: opt.value.toString(),
            label: opt.label,
          }))}
        />
        <FieldError errors={errors} field="departmentId" />
      </div>

      <div className="space-y-1.5">
        <Label>{t('instruments.form.unitField')} *</Label>
        <SearchableSelect
          value={form.data.unitId}
          onValueChange={(value) => form.setData('unitId', value)}
          disabled={!form.data.departmentId}
          placeholder={t('instruments.form.selectUnit')}
          options={filteredUnitOptions.map((opt) => ({
            value: opt.value.toString(),
            label: opt.label,
          }))}
        />
        <FieldError errors={errors} field="unitId" />
      </div>

      <div className="space-y-1.5">
        <Label>{t('instruments.form.tagsField')}</Label>
        <InstrumentTagCombobox
          selectedIds={form.data.tagIds}
          onChange={(ids) => form.setData('tagIds', ids)}
          initialTags={initialTags}
        />
      </div>

      {/* REBUILD-531: per-instrument Log Authentication override — only when the tenant has
          open logging on. Defaults to the instrument's current mode; blank inherits the tenant
          default ('OL'). 'OL' = open logging (no password), 'PR' = authenticated users only. */}
      {tenantOpenLoggingEnabled && (
        <div className="space-y-1.5">
          <Label htmlFor={fieldId('logAuthenticationCode')}>
            {t('instruments.form.logAuthentication')}
          </Label>
          <SearchableSelect
            id={fieldId('logAuthenticationCode')}
            value={form.data.logAuthenticationCode || 'OL'}
            onValueChange={(value) => form.setData('logAuthenticationCode', value)}
            placeholder={t('instruments.form.logAuthentication')}
            aria-label={t('instruments.form.logAuthentication')}
            options={[
              { value: 'OL', label: t('instruments.form.logAuthenticationOpen') },
              { value: 'PR', label: t('instruments.form.logAuthenticationAuthenticated') },
            ]}
          />
          <FieldError errors={errors} field="log_authentication_code" />
        </div>
      )}

      <div className="space-y-1.5">
        <Label>{t('schedule.loggingSchedule')}</Label>
        <WeeklyScheduleGrid
          value={form.data.customDaySchedule}
          onChange={(next) => form.setData('customDaySchedule', next)}
          followParent={form.data.followParentSchedule}
          onFollowParentChange={(checked) => form.setData('followParentSchedule', checked)}
          parentPreview={parentDaySchedule}
          parentPreviewPending={parentPreviewPending}
          parentPreviewFailed={parentPreviewFailed}
          onParentPreviewRetry={onParentPreviewRetry}
          parentLabel={t('schedule.followUnitSchedule')}
        />
        {errors.custom_day_schedule && (
          <p className="text-sm text-destructive">{errors.custom_day_schedule[0]}</p>
        )}
      </div>
    </>
  )
}

export default InstrumentFormFields
